import {
  filterMaybeArray,
  generateExamTicketCode,
  getStableExamAttemptId,
  getS3FileURL,
  getTagFromResource,
  getUniqueTags,
} from "@examspark/common";
import {
  AuthorizationContextInput,
  CreateExamAttemptInput,
  ExamAttempt,
  ExamBlobs,
  ExamConfigData,
  ExamSession,
  IDSK,
  JobExportContentFormat,
  ModelExamAttemptConnection,
  NoExtraProperties,
  ParsedExamAttempt,
  Replace,
  Resource,
  ResourceFile,
  ResourceMaturity,
  ResourceVisibilityType,
  SearchableResourceConnection,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
  UpdateExamAttemptInput,
} from "@examspark/types";

import { getAlignmentsSafely } from "../../../utils";
import { GraphQLAPI } from "../../client";
import { parseExamSession } from "../../deserializers";
import { alignmentsAbbrs } from "../../fragments/resource";
import { resource, search } from "../../queries";
import { getPlayableExamWithoutScoringQuery } from "../../queries/exam";
import { CopyExamDataInput, CreateExamConfigData } from "../../types";
import { createNumberedResource, createResource } from "../resource";

import { getExamDataForPlayer } from "./exam";
import { getExamConfigForDuplication } from "./examConfig";

export * from "./exam";
export * from "./examConfig";
export * from "./examSession";
export * from "./examTicket";

export const getExamSession = async (examSessionId: string) => {
  const result = await GraphQLAPI.query<{ getExamSession: ExamSession }>({
    query: /* GraphQL */ `
      query GetExamSession($input: ModelGetInput!) {
        getExamSession(input: $input) {
          id
          sk
          name
          maturity
          examSessionResponses
        }
      }
    `,
    variables: {
      input: {
        id: examSessionId,
        sk: "exam-session",
      },
    },
  });

  return result.data?.getExamSession;
};

export const getExamAttempt = async (
  examSessionId: string,
  attemptNumber: number,
) => {
  const result = await GraphQLAPI.query<{ getExamAttempt: ExamAttempt }>({
    query: /* GraphQL */ `
      query GetExamAttempt($input: ModelGetInput!) {
        getExamAttempt(input: $input) {
          id
          sk

          schemaVersion
          workspaceId

          examPosition {
            form
            section
            question
          }

          examSessionId
          attemptNumber
          source

          examSessionResponses
          examBlobs
          highlights
        }
      }
    `,
    variables: {
      input: {
        id: getStableExamAttemptId(examSessionId, attemptNumber),
        sk: `${examSessionId}/exam-session#exam-attempt`,
      },
    },
  });

  return result.data?.getExamAttempt;
};

export const listExamAttempts = async (examSessionId: string) => {
  // todo: Pageination?
  const result = await GraphQLAPI.query<{
    listExamAttempts: ModelExamAttemptConnection;
  }>({
    query: /* GraphQL */ `
      query ListExamAttempts($input: ListExamAttemptsInput!) {
        listExamAttempts(input: $input) {
          items {
            id
            sk

            examSessionId
            attemptNumber
          }
        }
      }
    `,
    variables: {
      input: {
        sk: `${examSessionId}/exam-session#exam-attempt`,
      },
    },
  });

  return result.data?.listExamAttempts;
};

// exam attempts with results and all the required props to save
export const listExamAttemptsAdmin = async (examSessionId: string) => {
  const result = await GraphQLAPI.query<{
    listExamAttempts: ModelExamAttemptConnection;
  }>({
    query: /* GraphQL */ `
      query ListExamAttempts($input: ListExamAttemptsInput!) {
        listExamAttempts(input: $input) {
          items {
            id
            sk
            workspaceId
            schemaVersion
            createdAt
            updatedAt

            examSessionId
            attemptNumber
            source

            examSessionResults
            examSessionResponses
            examBlobs
            highlights
          }
        }
      }
    `,
    variables: {
      input: {
        sk: `${examSessionId}/exam-session#exam-attempt`,
      },
    },
  });

  return result.data?.listExamAttempts;
};

export const createExamAttempt = async (
  input: NoExtraProperties<
    Replace<
      Omit<CreateExamAttemptInput, keyof IDSK>,
      // ensure they JSON stringify any AWSJSON types
      {
        examSessionResponses?: string | null;
        examSessionResults?: string | null;
      }
    >
  >,
) => {
  const result = await GraphQLAPI.query<{ createExamAttempt: ExamAttempt }>({
    query: /* GraphQL */ `
      mutation CreateExamAttempt($input: CreateExamAttemptInput!) {
        createExamAttempt(input: $input) {
          id
          sk

          attemptNumber
          source

          examSessionResponses
        }
      }
    `,
    variables: {
      input: {
        ...input,
        id: getStableExamAttemptId(input.examSessionId, input.attemptNumber),
        sk: `${input.examSessionId}/exam-session#exam-attempt`,
      },
    },
  });

  return result.data?.createExamAttempt;
};

export const updateExamAttempt = async (
  examAttempt: NoExtraProperties<UpdateExamAttemptInput>,
) => {
  const result = await GraphQLAPI.query<{ updateExamAttempt: ExamAttempt }>({
    query: /* GraphQL */ `
      mutation UpdateExamAttempt($input: UpdateExamAttemptInput!) {
        updateExamAttempt(input: $input) {
          id
          sk

          attemptNumber
          source

          examSessionResponses
        }
      }
    `,
    variables: {
      input: examAttempt,
    },
  });

  return result.data?.updateExamAttempt;
};

export const getPlayableExamStudent = async (examId: string) => {
  const result = await GraphQLAPI.query<{ getPlayableExam: Resource }>({
    query: getPlayableExamWithoutScoringQuery,
    auth: "API_KEY",
    variables: {
      input: {
        id: examId,
        sk: "exam#v0",
      },
    },
  });

  if (!result.data?.getPlayableExam) return;
  return result.data.getPlayableExam;
};

export const getExamSessionForCampfire = async (examSessionId: string) => {
  const result = await GraphQLAPI.query<{ getExamSession: ExamSession }>({
    query: /* GraphQL */ `
      query GetExamSession($input: ModelGetInput!) {
        getExamSession(input: $input) {
          id
          sk
          name
          maturity
          examSessionData {
            studentId
            lti {
              product
            }
          }
          currentAttemptNumber
          examSessionResponses
          examSessionResults
          examConfigId
          workspaceId
          createdAt
          updatedAt
          config
        }
      }
    `,
    variables: {
      input: {
        id: examSessionId,
        sk: "exam-session",
      },
    },
  });

  if (!result.data?.getExamSession) return null;
  const parsed = parseExamSession(result.data?.getExamSession);

  return parsed;
};

export const getExamSessionResults = async (examSessionId: string) => {
  const result = await GraphQLAPI.query<{ getExamSession: ExamSession }>({
    query: /* GraphQL */ `
      query GetExamSession($input: ModelGetInput!) {
        getExamSession(input: $input) {
          id
          sk
          name
          maturity
          examSessionResponses
          examSessionResults
        }
      }
    `,
    variables: {
      input: {
        id: examSessionId,
        sk: "exam-session",
      },
    },
  });

  return result.data?.getExamSession;
};

export const searchExamSessions = async (
  examConfigId: string,
  courseId?: string,
  workspaceId?: string,
) => {
  if (!workspaceId) return;
  const sessions: Resource[] = [];

  const _fetchSessions = async (nextToken?: string) => {
    const filter: SearchableResourceFilterInput = {
      examConfigId: {
        eq: examConfigId,
      },
      sk: {
        eq: "exam-session",
      },
      workspaceId: {
        matchPhrasePrefix: workspaceId,
      },
    };

    if (courseId) {
      filter.tags = {
        eq: `cohort-course-id#${courseId}`,
      };
    }

    const result = await GraphQLAPI.client.query<{
      searchUserResources: SearchableResourceConnection;
    }>({
      query: /* GraphQL */ `
        query FetchExamSessions(
          $filter: SearchableResourceFilterInput
          $sort: [SearchableResourceSortInput!]
          $aggs: [SearchableResourceAggsInput!]
          $limit: Int
          $nextToken: String
        ) {
          searchUserResources(
            filter: $filter
            sort: $sort
            aggs: $aggs
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              sk
              name
              maturity
              examSessionResponses
              examSessionResults
              resource {
                id
                sk
                name
              }
            }
            nextToken
          }
        }
      `,
      variables: {
        filter: filter,
        sort: [
          {
            field: "createdAt",
          },
        ],
        nextToken,
      },
    });

    if (!result.data) return;
    for (const session of result.data.searchUserResources?.items ?? []) {
      if (!session) continue;
      sessions.push(session);
    }
    if (result.data.searchUserResources.nextToken) {
      await _fetchSessions(result.data.searchUserResources.nextToken);
    }
  };

  await _fetchSessions();
  return sessions;
};

export const listExamConfigs = async (
  params: SearchableResourceFilterInput,
) => {
  const results = await GraphQLAPI.client.query<{
    searchUserResources: SearchableResourceConnection;
  }>({
    query: /* GraphQL */ `
      query SearchUserResources(
        $filter: SearchableResourceFilterInput
        $sort: [SearchableResourceSortInput!]
        $aggs: [SearchableResourceAggsInput!]
        $limit: Int
        $nextToken: String
      ) {
        searchUserResources(
          filter: $filter
          sort: $sort
          aggs: $aggs
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            idInc
            idVendor
            sk
            type
            version
            bankId
            name
            description
            author
            maturity
            subject
            grade
            language
            editorId
            creatorId
            questionIds
            contextIds
            schemaVersion
            createdAt
            updatedAt
            workspaceId
            alignmentIds
            isUserFavorite
            isUserWatcher
            ${alignmentsAbbrs}
            resource {
              id
              sk
              idInc
              name
              description
            }
          }
          nextToken
          total
          __typename
        }
      }
    `,
    variables: {
      filter: {
        ...params,
        sk: {
          eq: "exam-config",
        },
        isArchived: {
          ne: true,
        },
      },
      sort: [
        {
          field: "createdAt",
          direction: "desc",
        },
      ],
    },
  });

  return results.data?.searchUserResources;
};

export const getObjectiveMastery = async (params?: {
  workspaceId?: string;
  courseId?: string;
  examConfigCreatorId?: string;
}) => {
  const filter: SearchableResourceFilterInput = {
    sk: {
      wildcard: "exam-response*",
    },
    isArchived: {
      ne: true,
    },
    isDeleted: {
      ne: true,
    },
    and: [],
  };

  if (params?.examConfigCreatorId) {
    filter.and?.push({
      tags: {
        eq: `exam-config-creator-id#${params.examConfigCreatorId}`,
      },
    });
  }

  if (params?.courseId) {
    filter.and?.push({
      tags: {
        eq: `cohort-course-id#${params.courseId}`,
      },
    });
  }

  if (params?.workspaceId) {
    filter.and?.push({
      workspaceId: {
        eq: params.workspaceId,
      },
    });
  }

  const results = await GraphQLAPI.client.query<{
    search: SearchableResourceConnectionNew;
  }>({
    query: search.search({
      fields: `
        id
        idInc
        idVendor
        ${alignmentsAbbrs}
        resource {
          id
          sk
          idInc
          name
          description
        }
      `,
    }),
    variables: {
      filter,
      size: 0,
      aggsNew: [
        {
          name: "creatorId",
          type: "terms",
          field: "creatorId",
        },
        {
          name: "examConfigId",
          type: "terms",
          field: "examConfigId",
        },
        {
          name: "alignmentIds",
          type: "terms",
          field: "alignmentIds",
          aggs: [
            {
              name: "examSessionCount",
              type: "cardinality",
              field: "examSessionId",
            },
            {
              name: "responsesOverTime",
              type: "dateHistogram",
              field: "createdAt",
              interval: "1w",
              extendedBounds: {
                min: new Date(
                  // 10 weeks ago
                  Date.now() - 1000 * 60 * 60 * 24 * 7 * 12,
                ).toISOString(),
                max: new Date().toISOString(),
              },
              aggs: [
                {
                  name: "maxPoints",
                  type: "sum",
                  field: "scoreMax",
                },
                {
                  name: "actualPoints",
                  type: "sum",
                  field: "scoreActual",
                },
              ],
            },
          ],
        },
      ],
    },
  });

  return results.data?.search;
};

/** Copy an `exam`. */
export const copyExam = async (
  examKey: IDSK,
  newExamData: CopyExamDataInput,
) => {
  const existingExam = await getExamDataForPlayer({
    id: examKey.id,
    sk: examKey.sk,
  });

  if (!existingExam) throw new Error("An unknown error occurred.");
  const tags = [...(existingExam.tags ?? []), ...(newExamData.tags ?? [])];

  const createExamResult = await createNumberedResource({
    creatorId: newExamData.creatorId,
    workspaceId: newExamData.workspaceId,
    bankId: newExamData.bankId,
    content: existingExam.content,
    description: existingExam.description,
    difficulty: existingExam.difficulty,
    grade: existingExam.grade,
    maturity: existingExam.maturity ?? ResourceMaturity.Operational,
    name: newExamData.name ?? existingExam.name,
    questionIds: existingExam.questionIds,
    isUnlisted: newExamData.isUnlisted,
    visibility: newExamData.visibility,
    sk: existingExam.sk,
    tags,
    type: existingExam.type,
  });

  // refetch the exam so we have all the data we need later
  if (createExamResult.data?.createResource) {
    const refetchedExam = await getExamDataForPlayer({
      id: createExamResult.data?.createResource.id,
      sk: createExamResult.data?.createResource.sk,
    });

    // ...in the shape the caller expects
    return {
      data: {
        createResource: refetchedExam,
      },
    };
  }

  return createExamResult;
};

/**
 * Create an `exam-config` record. Optionally create a new `exam` record unless
 * `options.useOriginalExam` is enabled, in which case you're referencing the
 * existing exam directly. */
export const createExamFromExamTemplate = async (
  examKey: IDSK,
  examAndExamConfigData: Required<
    Pick<
      Resource,
      "id" | "appId" | "appKey" | "creatorId" | "workspaceId" | "bankId"
    >
  > & {
    tags: Resource["tags"];
    config?: Resource["config"];
    name?: Resource["name"];
  },
  options?: {
    /** Rarely used option to bind assignment (`exam-config`) directly to the
     * provided `exam`. Currently, only observed being used in Bonfire. */
    useOriginalExam?: boolean;
    createAccessCode?: boolean;
    ltiContextId?: string;
  },
) => {
  const getExamResult = await getExamDataForPlayer({
    id: examKey.id,
    sk: examKey.sk,
  });

  if (!getExamResult) throw new Error("An unknown error occurred.");

  let newExam: Resource | undefined;
  if (options?.useOriginalExam) {
    newExam = getExamResult;
  } else {
    newExam = (
      await copyExam(examKey, {
        ...examAndExamConfigData,
        tags: [
          ...(examAndExamConfigData.tags ?? []),
          ...[
            {
              key: "origin-exam-id",
              type: "string",
              value: examKey.id,
            },
          ],
        ],
        // Hide cloned exams from any/all search results
        // isUnlisted: true,
        visibility: ResourceVisibilityType.Private,
      })
    ).data?.createResource;
  }

  if (!newExam) {
    throw new Error(
      "An unknown error occurred while creating a new exam from a template.",
    );
  }

  const alignmentIds = filterMaybeArray(newExam?.questions)
    ?.map((q) => getAlignmentsSafely(q.alignments)?.map((a) => a.id))
    .flat();

  const examConfigTags = getUniqueTags([
    ...(getExamResult.tags ?? []),
    ...(examAndExamConfigData.tags ?? []),
    ...[
      {
        key: "origin-exam-id",
        type: "string",
        value: examKey.id,
      },
    ],
  ]);

  /** Create `exam-config` */
  const createExamConfigResult = await createExamConfig(
    {
      id: examAndExamConfigData.id,
      alignmentIds: alignmentIds,
      appId: examAndExamConfigData.appId,
      appKey: examAndExamConfigData.appKey,
      config: examAndExamConfigData.config,
      creatorId: examAndExamConfigData.creatorId,
      maturity: "IN_PROGRESS",
      name: examAndExamConfigData.name ?? getExamResult.name ?? "",
      resourceId: `${newExam?.id}/exam#v0`,
      tags: examConfigTags,
      workspaceId: examAndExamConfigData.workspaceId,
    },
    {
      createAccessCode: options?.createAccessCode,
    },
  );

  if (!createExamConfigResult.data?.createResource) {
    throw new Error("An unknown error occurred.");
  }

  const getNewExamConfigResult = await getExamConfigForDuplication({
    id: createExamConfigResult.data.createResource.id,
    sk: createExamConfigResult.data.createResource.sk,
  });

  if (options?.ltiContextId) {
    const creatorId = examAndExamConfigData.creatorId;
    const workspaceId = examAndExamConfigData.workspaceId;

    if (creatorId != null && workspaceId != null) {
      // NOTE: This is the ORIGINAL exam id, NOT the newly created ID.
      // so that we can find it via the picker later. The copied exam IDs
      // dont actually exist in a bank anywhere so having their ID is not helpful.
      // This means that lti-link.id !== lti-link.examConfig.exam.id
      await createLtiLink(examKey.id, options.ltiContextId, {
        examConfigId: createExamConfigResult.data.createResource.id,
        creatorId,
        workspaceId,
      });
    }
  }

  return getNewExamConfigResult;
};

export const readExamConfigConfig = async (
  examConfigId: string,
): Promise<ExamConfigData | undefined> => {
  const result = await GraphQLAPI.client.query<{ getResource: Resource }>({
    query: /* GraphQL */ `
      query GetExamConfig($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          id
          sk
          config
        }
      }
    `,
    variables: {
      id: examConfigId,
      sk: "exam-config",
    },
  });

  if (!result.data?.getResource) return;
  if (!result.data?.getResource.config) return {};
  if (typeof result.data?.getResource.config === "string") {
    return JSON.parse(result.data?.getResource.config);
  }

  return result.data?.getResource.config;
};

export const setExamConfig = async (
  examConfigId: string,
  data: ExamConfigData,
) => {
  await GraphQLAPI.client.query<{ updateResource: Resource }>({
    query: /* GraphQL */ `
      mutation UpdateExamConfig($input: UpdateResourceInput!) {
        updateResource(input: $input) {
          id
          sk
          config
          schemaVersion
          createdAt
          updatedAt
        }
      }
    `,
    variables: {
      input: {
        id: examConfigId,
        sk: ["exam-config"].join("#"),
        schemaVersion: "2020-01-01",
        config: JSON.stringify(data),
      },
    },
  });
};

export const updateExamConfig = async (
  examConfigId: string,
  data: ExamConfigData,
) => {
  const existingConfig = await readExamConfigConfig(examConfigId);
  if (!existingConfig) {
    console.warn(`Exam config ${examConfigId} not found`);
    return;
  }

  await GraphQLAPI.client.query<{ updateResource: Resource }>({
    query: /* GraphQL */ `
      mutation UpdateExamConfig($input: UpdateResourceInput!) {
        updateResource(input: $input) {
          id
          sk
          config
          updatedAt
        }
      }
    `,
    variables: {
      input: {
        id: examConfigId,
        sk: "exam-config",
        schemaVersion: "2020-01-01",
        config: JSON.stringify({
          ...existingConfig,
          ...data,
        }),
      },
    },
  });
};

export const createExamConfig = async (
  data: CreateExamConfigData,
  options?: {
    createAccessCode?: boolean;
  },
) => {
  const ticketId = generateExamTicketCode();
  const uniqueAlignmentIds = [...new Set(data.alignmentIds)];
  for (const tag of [
    "classroom-id",
    "cohort-classroom",
    "cohort-school",
    "unit-id",
  ]) {
    if (getTagFromResource(data, "cohort-classroom")?.value == null) {
      throw new Error(`${tag} tag is required`);
    }
  }

  if (typeof data.config !== "string") {
    data.config = JSON.stringify(data.config);
  }

  if (options?.createAccessCode) {
    if (!data.tags) data.tags = [];
    data.tags.push({
      key: "exam-ticket",
      type: "string",
      value: ticketId,
    });
  }

  const createExamConfigResult = await createNumberedResource({
    ...data,
    alignmentIds: uniqueAlignmentIds,
    sk: "exam-config",
  });

  if (options?.createAccessCode) {
    await createExamTicket(
      {
        workspaceId: data.workspaceId,
        creatorId: data.creatorId,
        examConfigId: createExamConfigResult.data?.createResource.id ?? "",
        tags: [
          ...(data.tags ?? []),
          {
            key: "exam-ticket",
            type: "string",
            value: ticketId,
          },
        ],
      },
      {
        code: ticketId,
      },
    );
  }

  return createExamConfigResult;
};

export const createExamTicket = async (
  data: Pick<Resource, "workspaceId" | "creatorId" | "tags"> & {
    examConfigId: string;
  },
  options?: {
    code?: string;
  },
) => {
  const ticketId = generateExamTicketCode();

  await createResource({
    variables: {
      id: options?.code ?? ticketId,
      sk: `exam-ticket`,
      schemaVersion: "2020-01-01",
      workspaceId: data.workspaceId,
      creatorId: data.creatorId,
      resourceId: [data.examConfigId, "exam-config"].join("/"),
      tags: [
        ...(data.tags || []),
        {
          key: "is-public",
          type: "string",
          value: "true",
        },
      ],
    },
  });

  return ticketId;
};

export const createExamExport = async (
  params: {
    id: string;
    format?: JobExportContentFormat;
  },
  authorizationContext: AuthorizationContextInput,
) => {
  return GraphQLAPI.client
    .query<{ createExamExportAction: Resource }>({
      query: /* GraphQL */ `
        mutation CreateExamExportAction($input: ControllerInput!) {
          createExamExportAction(input: $input) {
            id
            sk
            file {
              region
              bucket
              key
            }
          }
        }
      `,
      variables: {
        input: {
          action: "createExamExport",
          params: JSON.stringify({
            examId: params.id,
            format: params.format,
            workspaceId: authorizationContext.workspaceId,
          }),
        },
      },
    })
    .then((result) => result.data?.createExamExportAction ?? null);
};

export const duplicateExam = async (params: { examId: string }) => {
  return GraphQLAPI.client
    .query<{ duplicateExam: Resource }>({
      query: /* GraphQL */ `
        mutation DuplicateExam($input: ControllerInput!) {
          duplicateExam(input: $input) {
            id
            sk
          }
        }
      `,
      variables: {
        input: {
          action: "duplicateExam",
          params: JSON.stringify({
            examId: params.examId,
          }),
        },
      },
    })
    .then((result) => result.data?.duplicateExam ?? null);
};

export const getLtiLink = async (
  examId: string,
  ltiContextId: string,
): Promise<Resource | null> => {
  const result = await GraphQLAPI.query<{ getResource: Resource }>({
    query: /* GraphQL */ `
      query GetLtiLink($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          id
          sk
          examConfigId
          creatorId
          workspaceId
        }
      }
    `,
    variables: {
      id: examId,
      sk: `lti-link:${ltiContextId}`,
    },
  });

  return result.data?.getResource ?? null;
};

export const createLtiLink = async (
  examId: string,
  ltiContextId: string,
  data: {
    examConfigId: string;
    creatorId: string;
    workspaceId: string;
  },
) => {
  await createResource({
    variables: {
      id: examId,
      sk: `lti-link:${ltiContextId}`,
      type: "Resource",
      examConfigId: data.examConfigId,
      creatorId: data.creatorId,
      workspaceId: data.workspaceId,
    },
  });
};

export const deleteLtiLink = async (
  examId: string,
  ltiContextId: string,
  workspaceId: string,
) => {
  return GraphQLAPI.query<{
    deleteResource: Resource;
  }>({
    query: resource.deleteResource(),
    variables: {
      input: {
        id: examId,
        sk: `lti-link:${ltiContextId}`,
        workspaceId: workspaceId,
      },
    },
  });
};

//todo: move this?
export const getExamBlobsFromExamAttempt = async (
  examAttempt: ParsedExamAttempt,
) => {
  const blobFiles: Array<{
    questionId: string;
    interactionId: string;
    blobId: string;
    file: ResourceFile;
  }> = [];
  for (const [questionId, interactionBlobs] of Object.entries(
    examAttempt.examBlobs ?? {},
  )) {
    for (const [interactionId, blobIds] of Object.entries(interactionBlobs)) {
      for (const [blobId, file] of Object.entries(blobIds)) {
        blobFiles.push({
          questionId,
          interactionId,
          blobId,
          file,
        });
      }
    }
  }
  const blobs: ExamBlobs = {};
  await Promise.all(
    blobFiles.map(async (blob) => {
      // todo: consider requiring presigned URLS for GETing as well
      const s3Url = getS3FileURL(blob.file);

      const response = await fetch(s3Url, {
        method: "GET",
      });
      if (!response.ok) {
        return;
      }
      const data = await response.arrayBuffer();
      if (!blobs[blob.questionId]) {
        blobs[blob.questionId] = {};
      }
      if (!blobs[blob.questionId][blob.interactionId]) {
        blobs[blob.questionId][blob.interactionId] = {};
      }
      blobs[blob.questionId][blob.interactionId][blob.blobId] = data;
    }),
  );
  return blobs;
};

export const getPresignedUrlsForExamBlobs = async (
  examConfigId: string,
  // this may still be the examSessionId in places we don't
  // have an attempt (TbC exam player)
  examAttemptId: string,
  blobs: Array<{
    questionId: string;
    interactionId: string;
    blobId: string;
    size: number;
  }>,
) => {
  const result = await GraphQLAPI.query<{
    getPresignedUrlsForExamBlobs: {
      questionId: string;
      interactionId: string;
      blobId: string;
      presignedUrl: string;
    }[];
  }>({
    query: /* GraphQL */ `
      mutation GetPresignedUrlsForExamBlobs($input: ControllerInput!) {
        getPresignedUrlsForExamBlobs(input: $input) {
          questionId
          interactionId
          blobId
          presignedUrl
        }
      }
    `,
    variables: {
      input: {
        action: "getPresignedUrlsForExamBlobs",
        params: JSON.stringify({
          examConfigId,
          examAttemptId,
          blobs,
        }),
      },
    },
  });

  return result.data?.getPresignedUrlsForExamBlobs ?? [];
};
